import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Result, Button } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { exchangeCodeForTokens, getAndClearRedirectPath } from '../../utils/oauth';
import { resolvePostLoginRedirect } from '../../utils/persona';

const Callback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthTokens, logout } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    const processCallback = async (): Promise<void> => {
      try {
        console.log('Callback processing started', {
          url: window.location.href,
          hasCode: !!searchParams.get('code'),
          hasState: !!searchParams.get('state'),
          hasError: !!searchParams.get('error')
        });

        // Extract code and state from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors from Cognito
        if (errorParam) {
          // AccessDenied usually means callback URL mismatch
          if (errorParam === 'access_denied') {
            const message = errorDescription || 'Access denied. This usually means the callback URL does not match what is configured in Cognito.';
            console.error('Cognito AccessDenied error:', {
              error: errorParam,
              description: errorDescription,
              currentUrl: window.location.href,
              expectedCallback: `${window.location.origin}/callback`
            });
            throw new Error(message);
          }
          throw new Error(errorDescription || errorParam || 'Authentication failed');
        }

        // Check for required parameters
        if (!code) {
          throw new Error('Authorization code not found in callback');
        }

        if (!state) {
          throw new Error('State parameter not found in callback');
        }

        // Exchange code for tokens
        console.log('Attempting token exchange...');
        const tokenResponse = await exchangeCodeForTokens(code, state);
        if (cancelled) {
          return;
        }
        console.log('Token exchange successful');

        // Save tokens to context (which will persist to localStorage)
        // refresh_token is optional - may not be included in all responses
        setAuthTokens(
          tokenResponse.access_token,
          tokenResponse.id_token,
          tokenResponse.refresh_token
        );

        const redirectPath = resolvePostLoginRedirect(getAndClearRedirectPath());
        navigate(redirectPath, { replace: true });
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Callback processing error:', err);
        console.error('Error details:', {
          error: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
          localStorageTokens: {
            hasAccessToken: !!localStorage.getItem('auth_access_token'),
            hasIdToken: !!localStorage.getItem('auth_id_token'),
            hasRefreshToken: !!localStorage.getItem('auth_refresh_token')
          }
        });
        
        // Clear login in progress flag and set failed flag
        // This ensures old tokens don't make the user appear logged in after a failed login
        sessionStorage.removeItem('auth_login_in_progress');
        sessionStorage.setItem('auth_login_failed', 'true');
        
        // Log before clearing to see what we're clearing
        const tokensBeforeClear = {
          accessToken: localStorage.getItem('auth_access_token') ? 'present' : 'missing',
          idToken: localStorage.getItem('auth_id_token') ? 'present' : 'missing',
          refreshToken: localStorage.getItem('auth_refresh_token') ? 'present' : 'missing'
        };
        console.log('Clearing auth state. Tokens before clear:', tokensBeforeClear);
        
        logout();
        
        // Verify tokens are cleared
        const tokensAfterClear = {
          accessToken: localStorage.getItem('auth_access_token') ? 'present' : 'missing',
          idToken: localStorage.getItem('auth_id_token') ? 'present' : 'missing',
          refreshToken: localStorage.getItem('auth_refresh_token') ? 'present' : 'missing'
        };
        console.log('Tokens after clear:', tokensAfterClear);
        
        setError(err instanceof Error ? err.message : 'Failed to complete authentication');
        setIsProcessing(false);
      }
    };

    processCallback();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, setAuthTokens, logout]);

  if (isProcessing) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <Spin size="large" tip="Completing authentication..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        padding: '20px'
      }}>
        <Result
          status="error"
          title="Authentication Failed"
          subTitle={error}
          extra={[
            <Button type="primary" key="home" onClick={() => navigate('/')}>
              Go Home
            </Button>,
            <Button key="retry" onClick={() => navigate('/')}>
              Try Again
            </Button>
          ]}
        />
      </div>
    );
  }

  return <></>;
};

export default Callback;
